import { Space, Tag, Tooltip, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { shortId } from '@/shared/lib/shortId'
import { fetchAgent } from './agentApi'

interface AgentRefProps {
  agentId: string | null
  /** Kod ham ko'rsatilsinmi — jadval ustunida ha, tor joyda yo'q */
  showCode?: boolean
}

/**
 * Agentga havola — id o'rniga NOM.
 *
 * <h2>Nega kerak</h2>
 * Vaucher partiyalari ro'yxatida "Agent" ustunida xom UUID turardi
 * (`2e97386c…`). Operator qaysi agentga karta berilganini BILA OLMASDI —
 * ya'ni ustun bor edi, ma'lumot esa yo'q.
 *
 * <h2>⚠️ Nega xarita emas, har id uchun alohida so'rov</h2>
 * Agent moduli "hammasini ber" endpointiga EGA EMAS: `GET /api/agents`
 * majburiy `q` qidiruv so'zini talab qiladi (`agentApi.searchAgents`), va
 * bu ataylab — daraxt katta bo'lishi mumkin. Shuning uchun bu yerda
 * `GET /api/agents/{id}` ishlatiladi. N+1 emas: React Query bir xil
 * kalitni deduplikatsiya qiladi, ya'ni bitta agent jadvalda o'n marta
 * uchrasa ham SO'ROV BITTA bo'ladi.
 *
 * <h2>⚠️ Uch holat ALOHIDA — `SiteRef` bilan bir xil qoida</h2>
 *   · `agentId === null` → chiziqcha ("agentsiz partiya" — bu normal)
 *   · huquq yo'q         → qisqartirilgan id, sababi tooltipda.
 *                          "Noma'lum" deb AYBLANMAYDI: agent bor,
 *                          shunchaki bizga ko'rinmaydi
 *   · topilmadi          → sariq ogohlantirish
 */
export function AgentRef({ agentId, showCode = true }: AgentRefProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const allowed = can('agent.read')

  const query = useQuery({
    queryKey: queryKeys.agents.detail(agentId ?? ''),
    queryFn: () => fetchAgent(agentId as string),
    enabled: allowed && agentId !== null,
    // Agent nomi sessiya davomida deyarli o'zgarmaydi — jadval har
    // yangilanganda qayta so'rash keraksiz
    staleTime: 5 * 60_000,
    retry: false,
    /*
      ⚠️ OSILIB QOLGAN HAVOLA QAYTA-QAYTA SO'RALMAYDI (XSB-006).

      O'lchov (2026-08-13, Staging): `/vouchers` sahifasida ikkita partiya
      mavjud bo'lmagan agentga ishora qiladi va har ochilishda ikkita
      `404 GET /api/agents/{id}` ketardi. Sahifaning o'zi ISHLAYDI —
      ustunda sariq «Agent topilmadi» chiqadi — lekin brauzer konsoli
      qizil xatoga to'lardi va tashqi tekshiruvchi shuni "vaucherlar
      ishlamaydi" deb o'qidi.

      `retry: false` faqat BIR so'rov ichidagi takrorni to'xtatadi.
      `retryOnMount: false` esa komponent qayta ulanganda (boshqa sahifaga
      o'tib qaytganda, jadval qayta chizilganda) YANA so'ramaydi: yo'q
      yozuv bir daqiqada paydo bo'lib qolmaydi, ya'ni takroriy so'rov
      hech qachon boshqa javob keltirmasdi.

      ⚠️ Bu YASHIRISH EMAS: birinchi 404 baribir ketadi va natija ekranda
      ochiq aytiladi. Bazadagi osilgan havolani tozalash alohida ish
      (`V202608140500` migratsiyasi).
    */
    retryOnMount: false,
  })

  if (agentId === null) {
    return <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
  }

  if (!allowed) {
    return (
      <Tooltip title={t('agents.refNoPermission')}>
        <Typography.Text type="secondary" code style={{ fontSize: 12 }}>
          {/* Qisqartirish YAGONA joyda — `shared/lib/shortId.ts` (B12).
              Ilgari bu yerda uchsiz `slice(0, 8)` turardi, `AuditPage` da
              esa uchli shakl: bir aktyor ikki ekranda ikki xil ko'rinardi. */}
          {shortId(agentId)}
        </Typography.Text>
      </Tooltip>
    )
  }

  if (query.isPending) {
    return <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
  }

  if (query.isError || !query.data) {
    /*
      ⚠️ Tooltip'da ilgari FAQAT xom id turardi. U hech nima tushuntirmasdi:
      operator sariq tegni ko'rib "nimadir buzilibdi" deb o'ylardi, holbuki
      partiyaning o'zi butun va u bilan ishlash mumkin. Endi matn nima
      bo'lganini va nima qilish kerakligini aytadi (XSB-006).
    */
    return (
      <Tooltip title={t('agents.refUnknownHint')}>
        <Tag color="warning">{t('agents.refUnknown')}</Tag>
      </Tooltip>
    )
  }

  return (
    <Space size={4} wrap>
      <Typography.Text>{query.data.displayName}</Typography.Text>
      {showCode ? <Tag>{query.data.code}</Tag> : null}
    </Space>
  )
}
